import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, uploadDocument } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Paperclip, X } from "lucide-react";
import { PO_INVOICE_META } from "@/lib/poStatus";

const label = "text-[11px] font-semibold uppercase tracking-wide text-slate-500";

export default function PoInvoiceDialog({ ctx, onClose, onSaved }) {
  const po = ctx?.po;
  const existing = ctx?.invoice || null;
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(null);

  useEffect(() => {
    if (!po) return;
    setF(existing ? { ...existing } : {
      invoice_number: "", invoice_date: "", due_date: "", amount: "",
      currency: po.currency || "USD", payment_terms: po.payment_terms || "",
      status: "unpaid", paid_amount: "", notes: "", docs: [],
    });
  }, [po, existing]);

  if (!po || !f) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onPick = async (e) => {
    const fs = Array.from(e.target.files || []);
    e.target.value = "";
    if (!fs.length) return;
    setBusy(true);
    try {
      const arr = [...(f.docs || [])];
      for (const file of fs) {
        const d = await uploadDocument(file);
        arr.push({ name: d.name || file.name, url: d.url, path: d.path });
      }
      set("docs", arr);
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, amount: Number(f.amount) || 0, paid_amount: Number(f.paid_amount) || 0 };
      if (existing) await api.put(`/po-invoices/${existing.id}`, payload);
      else await api.post(`/purchase-orders/${po.id}/invoices`, payload);
      toast.success(existing ? "Invoice updated" : `Invoice ${f.invoice_number} added to ${po.po_number}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save the invoice");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="po-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold">
            {existing ? `Edit Invoice ${existing.invoice_number}` : `Add Supplier Invoice — ${po.po_number}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-500">{po.supplier_name} · PO total <span className="font-semibold text-slate-700">${Number(po.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className={label}>Supplier Invoice #</Label>
              <Input value={f.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} placeholder="INV-1234" className="h-9 rounded-sm" data-testid="po-invoice-number" autoFocus />
            </div>
            <div className="space-y-1">
              <Label className={label}>Amount</Label>
              <Input type="text" inputMode="decimal" value={f.amount} onChange={(e) => set("amount", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" className="h-9 rounded-sm" data-testid="po-invoice-amount" />
            </div>
            <div className="space-y-1">
              <Label className={label}>Invoice Date</Label>
              <Input type="date" value={f.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} className="h-9 rounded-sm" data-testid="po-invoice-date" />
            </div>
            <div className="space-y-1">
              <Label className={label}>Due Date</Label>
              <Input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className="h-9 rounded-sm" data-testid="po-invoice-due" />
            </div>
            <div className="space-y-1">
              <Label className={label}>Payment Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="po-invoice-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["unpaid", "partially_paid", "paid", "disputed"].map((s) => (
                    <SelectItem key={s} value={s}>{PO_INVOICE_META[s].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {f.status === "partially_paid" ? (
              <div className="space-y-1">
                <Label className={label}>Paid So Far</Label>
                <Input type="text" inputMode="decimal" value={f.paid_amount} onChange={(e) => set("paid_amount", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" className="h-9 rounded-sm" data-testid="po-invoice-paid-amount" />
              </div>
            ) : (
              <div className="space-y-1">
                <Label className={label}>Payment Terms</Label>
                <Input value={f.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} placeholder="NET 30" className="h-9 rounded-sm" data-testid="po-invoice-terms" />
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Label className={label}>Invoice PDF / Attachments</Label>
            <div className="flex flex-wrap items-center gap-2">
              {(f.docs || []).map((d, i) => (
                <span key={`${d.path}-${i}`} className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
                  <a href={d.url} target="_blank" rel="noreferrer" className="max-w-[160px] truncate text-[#002FA7] hover:underline">{d.name}</a>
                  <button type="button" onClick={() => set("docs", f.docs.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                </span>
              ))}
              <label className="inline-flex cursor-pointer items-center gap-1 rounded-sm border border-dashed border-slate-300 px-2 py-1 text-xs font-semibold text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]" data-testid="po-invoice-attach">
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />} Attach
                <input type="file" className="hidden" multiple onChange={onPick} />
              </label>
            </div>
          </div>
          <div className="space-y-1">
            <Label className={label}>Notes</Label>
            <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="rounded-sm" data-testid="po-invoice-notes" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="po-invoice-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="po-invoice-save">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{existing ? "Save Changes" : "Add Invoice"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
